import { useCallback, useEffect, useRef, useState } from "react";
import { X, ShieldCheck, ShieldAlert, Mail, Smartphone, Trash2, Plus, Loader2, CheckCircle2 } from "lucide-react";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../../firebase";
import SmsCodeStep from "./SmsCodeStep";
import {
    makeRecaptcha, sendEnrolCode, finishEnrol, phonesOf, removePhone, confirmPassword, maskPhone,
    normalisePhone, mfaMessage, needsRecentLogin, needsSecondFactor, resolverFor,
} from "../../auth/mfa";

const btn = "px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2";
const primary = `${btn} bg-teal-600 hover:bg-teal-700 text-white`;
const secondary = `${btn} bg-white border border-slate-200 text-slate-700 hover:bg-slate-50`;

/**
 * Security screen: two-step sign-in by text message (optional). Verify the email address,
 * add phones (two recommended, so losing one doesn't lock the account), remove phones.
 * Changes need a recent sign-in: when Firebase asks for one, the password (and SMS code, if
 * two-step is already on) is confirmed here and the change is finished.
 */
export default function SecuritySettings({ onClose }) {
    const user = auth.currentUser;
    const [, refresh] = useState(0);
    const rerender = () => refresh(n => n + 1);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);

    // A change that is waiting for the user to confirm it's them
    const [pending, setPending] = useState(null);       // () => Promise
    const [password, setPassword] = useState("");
    const [reauthResolver, setReauthResolver] = useState(null);

    // Adding a phone
    const [adding, setAdding] = useState(false);
    const [phone, setPhone] = useState("+61 ");
    const [label, setLabel] = useState("");
    const [verificationId, setVerificationId] = useState(null);
    const [code, setCode] = useState("");
    const containerId = useRef(`recaptcha-${Math.random().toString(36).slice(2)}`).current;
    const verifier = useRef(null);

    useEffect(() => { user?.reload().then(rerender).catch(() => {}); return () => verifier.current?.clear(); }, [user]);

    /** Run a change; if Firebase wants a recent sign-in, ask for it and then run the change again */
    const withRecentLogin = useCallback(async action => {
        setError(""); setBusy(true);
        try {
            await action();
        } catch (err) {
            if (needsRecentLogin(err)) { setPending(() => action); setPassword(""); }
            else console.warn("Two-step sign-in:", err.code, err.message); setError(mfaMessage(err));
        } finally {
            setBusy(false);
        }
    }, []);

    const resumePending = async () => {
        const action = pending;
        setPending(null); setReauthResolver(null);
        await withRecentLogin(action);
    };

    const confirmIdentity = async e => {
        e.preventDefault();
        setError(""); setBusy(true);
        try {
            await confirmPassword(user, password);
            await resumePending();
        } catch (err) {
            if (needsSecondFactor(err)) setReauthResolver(resolverFor(err));
            else console.warn("Two-step sign-in:", err.code, err.message); setError(mfaMessage(err));
        } finally {
            setBusy(false);
        }
    };

    if (!user) return null;
    const phones = phonesOf(user);

    const sendVerification = () => withRecentLogin(async () => {
        await sendEmailVerification(user);
        setNotice(`We've emailed a verification link to ${user.email}. Open it, then click "I've verified it".`);
    });
    const checkVerified = () => withRecentLogin(async () => {
        await user.reload();
        rerender();
        setNotice(auth.currentUser.emailVerified ? "Email verified." : "Not verified yet. Open the link in the email first.");
    });

    const sendCode = () => withRecentLogin(async () => {
        verifier.current?.clear();
        verifier.current = makeRecaptcha(containerId);
        setVerificationId(await sendEnrolCode(user, normalisePhone(phone), verifier.current));
        setCode("");
    });
    const confirmCode = e => {
        e.preventDefault();
        withRecentLogin(async () => {
            await finishEnrol(user, verificationId, code.trim(), label.trim() || "Mobile");
            await user.reload(); rerender();
            setAdding(false); setVerificationId(null); setPhone("+61 "); setLabel("");
            setNotice(phonesOf(auth.currentUser).length === 1
                ? "Two-step sign-in is on. Add a second phone too, so losing this one doesn't lock you out."
                : "Phone added.");
        });
    };

    const remove = factor => {
        const last = phones.length === 1;
        if (!window.confirm(last
            ? `Remove ${maskPhone(factor.phoneNumber)}? This turns two-step sign-in off for your account.`
            : `Remove ${maskPhone(factor.phoneNumber)}?`)) return;
        withRecentLogin(async () => {
            await removePhone(user, factor);
            await user.reload(); rerender();
            setNotice(last ? "Two-step sign-in is off." : "Phone removed.");
        });
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        {phones.length ? <ShieldCheck size={20} className="text-teal-600" /> : <ShieldAlert size={20} className="text-slate-400" />}
                        <h2 className="text-lg font-bold text-slate-800">Security</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg" title="Close"><X size={18} /></button>
                </div>

                <div className="p-6 space-y-6">
                    {error && <p className="text-sm text-red-600 font-semibold">{error}</p>}
                    {notice && !error && <p className="text-sm text-teal-700 bg-teal-50 rounded-lg px-3 py-2">{notice}</p>}

                    {pending ? (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-600">For your security, confirm it's you to make this change.</p>
                            {reauthResolver ? (
                                <SmsCodeStep resolver={reauthResolver} onDone={resumePending} onCancel={() => { setPending(null); setReauthResolver(null); }} />
                            ) : (
                                <form onSubmit={confirmIdentity} className="space-y-3">
                                    <input type="password" autoFocus placeholder="Your password" className="w-full p-3 border border-gray-300 rounded-md"
                                        value={password} onChange={e => setPassword(e.target.value)} required />
                                    <div className="flex gap-2">
                                        <button type="submit" disabled={busy} className={`${primary} flex-1`}>{busy && <Loader2 size={16} className="animate-spin" />} Confirm</button>
                                        <button type="button" onClick={() => setPending(null)} className={secondary}>Cancel</button>
                                    </div>
                                </form>
                            )}
                        </div>
                    ) : (
                        <>
                            <section className="space-y-2">
                                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Mail size={15} /> Email</h3>
                                <p className="text-sm text-slate-600">{user.email} · {user.emailVerified
                                    ? <span className="text-teal-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={14} /> verified</span>
                                    : <span className="text-amber-700 font-semibold">not verified</span>}</p>
                                {!user.emailVerified && (
                                    <>
                                        <p className="text-xs text-slate-500">Two-step sign-in needs a verified email address.</p>
                                        <div className="flex gap-2">
                                            <button onClick={sendVerification} disabled={busy} className={primary}>Send verification email</button>
                                            <button onClick={checkVerified} disabled={busy} className={secondary}>I've verified it</button>
                                        </div>
                                    </>
                                )}
                            </section>

                            <section className="space-y-3">
                                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Smartphone size={15} /> Two-step sign-in by text message</h3>
                                <p className="text-xs text-slate-500">
                                    {phones.length ? "On. Signing in asks for a code texted to one of these phones." : "Off. Turn it on to have signing in also ask for a code texted to your phone."}
                                </p>
                                {phones.map(f => (
                                    <div key={f.uid} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-800">{f.displayName || "Mobile"}</p>
                                            <p className="text-xs text-slate-500">{maskPhone(f.phoneNumber)} · added {new Date(f.enrollmentTime).toLocaleDateString("en-AU")}</p>
                                        </div>
                                        <button onClick={() => remove(f)} disabled={busy} className="p-2 text-slate-400 hover:text-red-600 rounded-lg disabled:opacity-50" title="Remove"><Trash2 size={16} /></button>
                                    </div>
                                ))}
                                {phones.length === 1 && <p className="text-xs text-amber-700">Add a second phone, so losing this one doesn't lock you out.</p>}

                                {!adding ? (
                                    <button onClick={() => { setAdding(true); setNotice(""); }} disabled={!user.emailVerified || busy} className={secondary}
                                        title={user.emailVerified ? "" : "Verify your email first"}>
                                        <Plus size={15} /> {phones.length ? "Add another phone" : "Turn on with my phone"}
                                    </button>
                                ) : !verificationId ? (
                                    <div className="space-y-2 rounded-xl border border-slate-200 p-3">
                                        <input inputMode="tel" placeholder="+61 412 345 678" className="w-full p-2.5 border border-gray-300 rounded-md"
                                            value={phone} onChange={e => setPhone(e.target.value)} />
                                        <input placeholder="Name for this phone (e.g. Work mobile)" className="w-full p-2.5 border border-gray-300 rounded-md"
                                            value={label} onChange={e => setLabel(e.target.value)} maxLength={40} />
                                        <p className="text-xs text-slate-500">We'll text a code to this number. Standard message rates may apply. Google stores the number to send sign-in codes and prevent abuse.</p>
                                        <div className="flex gap-2">
                                            <button onClick={sendCode} disabled={busy || normalisePhone(phone).length < 8} className={`${primary} flex-1`}>{busy && <Loader2 size={16} className="animate-spin" />} Text me a code</button>
                                            <button onClick={() => setAdding(false)} className={secondary}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <form onSubmit={confirmCode} className="space-y-2 rounded-xl border border-slate-200 p-3">
                                        <p className="text-sm text-slate-600">Enter the code sent to {normalisePhone(phone)}.</p>
                                        <input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code"
                                            className="w-full p-2.5 border border-gray-300 rounded-md tracking-[0.4em] text-center text-lg"
                                            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} required />
                                        <div className="flex gap-2">
                                            <button type="submit" disabled={busy || code.length !== 6} className={`${primary} flex-1`}>{busy && <Loader2 size={16} className="animate-spin" />} Confirm</button>
                                            <button type="button" onClick={sendCode} disabled={busy} className={secondary}>New code</button>
                                        </div>
                                    </form>
                                )}
                            </section>
                        </>
                    )}
                </div>
                <div id={containerId} />
            </div>
        </div>
    );
}
